import React, { useState, useEffect, useRef } from 'react';
import { questions, answers, retrieveMedia } from './data'
import './App.css';

function arraysEqual(a, b) {
  return a.length === b.length && a.every((val, i) => val === b[i]);
}

function includesArray(arr, target) {
  return arr.some(sub => arraysEqual(sub, target));
}

function secondLast(array) {
  return array[array.length - 2]
}

function last(array) {
  return array[array.length - 1]
}

function AudioClip({ url, start, duration }) {
  const audioRef = useRef(null);

  const playClip = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = start;
      audio.play();

      const stopTime = start + duration;
      const interval = setInterval(() => {
        if (audio.currentTime >= stopTime) {
          audio.pause();
          clearInterval(interval);
        }
      }, 100);
    }
  };

  return (
    <div>
      <div
        className="button reverse play-button"
        onClick={playClip}
      >
        play
      </div>
      <audio ref={audioRef} src={"/media/" + url} preload="auto" />
    </div>
  );
}

function VideoClip({ url, start = 0, duration = 5, muted = true }) {
  const [visible, setVisible] = useState(false);
  const videoRef = useRef(null);

  const openAndPlay = () => {
    setVisible(true);
    setTimeout(() => {
      const vid = videoRef.current;
      if (!vid) return;
      vid.currentTime = start;
      vid.muted = muted;
      vid.play();

      const stopAt = start + duration;
      const interval = setInterval(() => {
        if (vid.currentTime >= stopAt || vid.ended) {
          vid.pause();
          setVisible(false);
          clearInterval(interval);
        }
      }, 100);
    }, 100);
  };

  return (
    <>
      <div className="button reverse play-button" onClick={openAndPlay}>
        ▶ play {duration}s
      </div>

      {visible && (
        <div className="video-modal" onClick={() => setVisible(false)}>
          <div className="video-wrapper" onClick={e => e.stopPropagation()}>
            <video
              ref={videoRef}
              src={`/media/${url}`}
              preload="metadata"
              autoPlay
              playsInline
              muted={muted}
            />
          </div>
        </div>
      )}
    </>
  );
}

function getValue(question, completed, difficulty) {
  const multiplier = difficulty ?? 1

  const [cat, qn] = question;

  const sameCatCount = completed.filter(
    ([c, q]) => c === cat && q !== qn
  ).length;

  const baseValue = (qn + 1) * 100 * multiplier;

  return sameCatCount >= 4 ? baseValue * 2 : baseValue;
}

function App() {
  const [locked, setLocked] = useState(true)
  const [scores, setScores] = useState(JSON.parse(localStorage.getItem('scores')) || [0, 0, 0, 0])
  const [grid, setGrid] = useState(true)
  const [question, setQuestion] = useState([0, 0])
  const [team, setTeam] = useState(0)
  const [difficulty, setDifficulty] = useState(0)
  const [answer, setAnswer] = useState(false)
  const [completed, setCompleted] = useState(JSON.parse(localStorage.getItem('completed')) || [])
  const [betting, setBetting] = useState([0, 0, 0, 0])
  const [hover, setHover] = useState(null)

  const types = ["guess the song", "street dance"]
  const categories = ["from audio", "from choreo", "history", "in pop culture"]
  const questionColors = ["#6A3805", "#B4B4B4", "#AF9500", "#a6c9db", "#29f2ff"]
  const teamColors = ["red", "yellow", "green", "blue"]

  const clues = [
    ["clue type", "seconds", "points"],
    ["hard", 5, 1.5],
    ["regular", 1, 1],
    [2, 0.75],
    [3, 0.5],
    [5, 0.25],
    [10, 0],
  ]

  useEffect(() => {
    localStorage.setItem('scores', JSON.stringify(scores))
    localStorage.setItem('completed', JSON.stringify(completed))
  }, [scores, completed])

  useEffect(() => {
    const handleKey = (event) => {
      const key = event.key;

      const func = (prev) => {
        let newHover = hover
        while (newHover >= 4) {
          newHover -= 4
        }
        console.log(prev, newHover)

        const newScores = [...prev]
        const current = prev[newHover] || 0;

        let newScore = current
        if (key === 'Backspace') {
          newScore = Number(current.toString().slice(0, -1)) || 0;
        } else if (!isNaN(key)) {
          newScore = Number(current.toString() + key);
        }
        
        newScores[newHover] = newScore

        return newScores;
      }

      if (hover !== null) {
        if (hover < 4) {
          setScores(func);
        } else if (hover < 8) {
          setBetting(func)
        }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [team, setScores, hover]);

  if (completed.length >= 21) {
    return (
      <div className="app">
        <div className="app-header">
          <div className="title">jeopardy!</div> 
          <div className="subtitle">madstreet edition</div>
        </div>
        <div className="scores-big translucent-background">
          {
            scores
              .map((score, index) => ({ team: index + 1, score }))
              .sort((a, b) => b.score - a.score) // sort by score descending
              .map((entry, rank) => (
                <React.Fragment>
                  <div>#{rank+1}</div>
                  <div>team {entry.team}</div>
                  <div>{entry.score}</div>
                </React.Fragment>
              ))
          }
        </div>
      </div>
    )
  }

  function givePoints(value=null) {
    setScores(prev => {
      const newScores = [...prev]
      let pointsGained = 0
      if (question[0] >= 2) {
        pointsGained = value ?? getValue(question, completed)
      } else {
        let multiplier = last(clues[difficulty+1])
        pointsGained = value ?? getValue(question, completed, multiplier)
      }
      newScores[team] += pointsGained 
      return newScores
    })
    setCompleted(prev => {
      const newCompleted = [...prev];
      const questionStr = JSON.stringify(question);
      const alreadyExists = newCompleted.some(q => JSON.stringify(q) === questionStr);
      if (!alreadyExists) {
        newCompleted.push(question);
      }
      return newCompleted;
    });
  }

  function GuessTable(props) {
    return (
      <div className="guess-table">
        {
          clues.map(
            (row, i) => row.map(
              (cell, j) => (
                <div
                  key={`${i}-${j}`}
                  className={((difficulty + 1) === i || (i === 2 && j === 0 && difficulty)) ? "reverse" : undefined}
                  style={{
                    fontWeight: i ? "normal" : "bold",
                    gridRow: (i === 2 && !j) && "span 5",
                  }}
                  onClick={() => {setDifficulty(i-1)}}
                >{(!i || (j < row.length - 1)) ? cell : cell * props.value}</div>
              )
            )
          )
        }
      </div>
    )
  }

  function QuestionContent() {
    if (question[0] >= 2) {
      return (
        <div className="question-content">
          {questions[question[0]-2][question[1]].split('\n').map((line, idx) => (
            <React.Fragment key={idx}>
              {line}
              <br />
            </React.Fragment>
          ))}
        </div>
      )
    } else if (question[0] === 0) {
      const media = retrieveMedia(...question);
      return (
        <div className="media-settings">
          <GuessTable value={getValue(question, completed)} /> 
          <AudioClip 
            url={media.url} 
            start={difficulty ? media.regular : media.hard} 
            duration={secondLast(clues[difficulty + 1])} 
          />
        </div>
      )
    } else {
      const media = retrieveMedia(...question);
      return (
        <div className="media-settings">
          <GuessTable value={getValue(question, completed)} /> 
          <VideoClip 
            url={media.url} 
            start={difficulty ? media.regular : media.hard} 
            duration={secondLast(clues[difficulty + 1])} 
          />
        </div>
      )
    }
  }

  function QuestionInterface(props) {
    if (answer) {
      return (
        <div className="answer-content">
          {answers[props.cat][props.qn].split('\n').map((line, idx) => (
            <React.Fragment key={idx}>
              {line}
              <br />
            </React.Fragment>
          ))}
          {question[0] === 0 && (
            <AudioClip
              url={retrieveMedia(...question).url}
              start={retrieveMedia(...question).regular}
              duration={15}
            />
          )}
          {question[0] === 1 && (
            <VideoClip
              url={retrieveMedia(...question).url}
              start={retrieveMedia(...question).regular}
              duration={15}
              muted={false}
            />
          )}
        </div>
      );
    } else {
      return (
        props.cat === 4 ? (
          <React.Fragment>
            <div className="betting">
              {
                (betting.map((val, i) => (
                  <div
                    key={i}
                    type="number"
                    value={val}
                    onMouseEnter={() => {setHover(i+4)}}
                    onMouseLeave={() => {hover === (i+4) && setHover(null)}}
                  >{betting[i]}</div>
                )))
              }
            </div>
            <QuestionContent />
            <div className="reverse button" onClick={() => givePoints()}>give points</div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <QuestionContent />
            <div className="reverse button" onClick={() => givePoints()}>give points</div>
          </React.Fragment>
        )
      )
    }
  }

  function Body() {
    if (grid) {
      if (completed.length < 20) {
        return (
          <div className="question-grid translucent-background">
            {types.map(i => <div key={i} className="subtitle long">{i}</div>)}
            {categories.map(i => <div key={i} className="header">{i}</div>)}
            {[...Array(5)].map(
              (_, i) => [...Array(4)].map(
                (_, j) => (
                  <div
                    key={`${i}-${j}`}
                    className="question"
                    style={
                      includesArray(completed, [j, i]) ? {
                        backgroundColor: "#fff0",
                        color: "#fff0"
                      } : {
                        backgroundColor: questionColors[i],
                        color: i % 2 ? "black" : "white"
                      } 
                    }
                    onClick={() => {setQuestion([j, i]); setGrid(false)}}
                  >{getValue([j, i], completed)}</div>
                )
              )
            )}
          </div>
        )
      } else {
        return (
          <div 
            className="final-jeopardy" 
            style={{backgroundColor: "#7852ee"}}
            onClick={() => {setQuestion([4, 0]); setGrid(false)}}
          >
            final jeopardy
          </div>
        )
      }
    } else {
      const [cat, qn] = question;
      if (question[0] === 4) {
        return (
          <div className="question-page">
            <div className="question-metadata">final jeopardy - <span style={{color: "#7852ee"}}>??? points</span></div>
            <QuestionInterface cat={cat} qn={qn} />
            <div className="reverse button" onClick={() => {setAnswer(prev => !prev)}}>{answer ? "hide" : "show"} answer</div>
            <div className="reverse button" onClick={() => {setGrid(true); setAnswer(false)}}>back</div>
          </div>
        )
      }
      return (
        <div className="question-page">
          <div className="question-metadata">{types[Math.floor(cat/2)]} {categories[cat]} - <span style={{color: questionColors[qn]}}>{getValue([cat, qn], completed)} points</span></div>
          <QuestionInterface cat={cat} qn={qn} />
          <div className="reverse button" onClick={() => {setAnswer(prev => !prev)}}>{answer ? "hide" : "show"} answer</div>
          <div className="reverse button" onClick={() => {setGrid(true); setAnswer(false)}}>back</div>
        </div>
      )
    }
  }

  return (
    <div className="app">
      {locked && (
        <div className="overlay">
          <form onSubmit={
            (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const password = formData.get('password');
              if (password === "teertsdam") {
                setLocked(false);
              }
            }
          }>
            <input
              type="password"
              name="password"
              placeholder="Enter password"
            />
            <button type="submit">Unlock</button>
          </form>
        </div>
      )}
      <div className="app-header">
        <div className="title">jeopardy!</div> 
        <div className="subtitle">madstreet edition</div>
      </div>
      <div className="scores">
        {
          [...Array(scores.length)].map(
            (_, i) => <div 
              key={i}
              className={`header bold ${(team === i && i >= 2) ? "white-children" : undefined}`}
              style={{backgroundColor: team === i ? teamColors[i] : null}}
              onClick={() => {setTeam(i)}}
              onMouseEnter={() => {setHover(i)}}
              onMouseLeave={() => {hover === i && setHover(null)}}
            >
              <div>team {i+1}</div>
              <div>{scores[i]}</div>
            </div>
          )
        }
      </div>
      <Body />
    </div>
  );
}

export default App;
